/**
 * Updates a link by sending a PUT request to the API, and provides feedback to the user via a modal.
 *
 * @param {string} idLinks - The unique identifier of the link to be updated.
 * @param {string} originalLink - The new value for the original link.
 * @param {Object} router - The router object used for navigation.
 * @param {Function} swal - A function used to display modal messages to the user.
 * @return {Promise<void>} A promise that resolves when the operation is complete.
 */
export async function updateLink(idLinks, originalLink, router, swal) {

    const apiUrl = process.env.VUE_APP_API_URL;

    if (!idLinks) {
        swal({
            icon: 'error',
            title: 'Error!',
            text: "The 'link' parameter is not defined in the URL.",
            confirmButtonText: 'OK',
        });
        return;
    }

    /**
     * Represents the token retrieved from browser's session storage.
     * The token is typically used for authentication purposes or
     * to validate user sessions within the application.
     *
     * The value of the token is retrieved using the key "token"
     * from sessionStorage.
     *
     * Note: The token may be null if it does not exist in sessionStorage.
     */
    const token = sessionStorage.getItem("token");
    if (!token) {
        swal({
            icon: 'error',
            title: 'Error!',
            text: 'Token is not available in sessionStorage.',
            confirmButtonText: 'OK',
        });
        return;
    }

    /**
     * Represents the payload object containing link information.
     *
     * @typedef {Object} Payload
     * @property {Array} id_links - Array of associated ID links.
     * @property {string} original_link - The original link as a string.
     */
    const payload = {
        id_links: idLinks,
        original_link: originalLink,
    };

    try {
        const response = await fetch(`${apiUrl}/links/${idLinks}`, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            swal({
                icon: 'success',
                title: 'Updated!',
                text: 'The link was successfully updated.',
                confirmButtonText: 'OK',
            }).then(() => {
                router.push({ name: "Links" });
            });
        } else {
            swal({
                icon: 'error',
                title: 'Error!',
                text: 'The link could not be updated.',
                confirmButtonText: 'OK',
            });
        }
    } catch (error) {
        swal({
            icon: 'error',
            title: 'Error!',
            text: 'An error occurred while updating the link.',
            confirmButtonText: 'OK',
        });
        console.error("Error updating link:", error);
    }
}
